import { AxiosError, AxiosHeaders, isAxiosError } from 'axios';

export type HttpFailureKind =
  /** The server answered 404. */
  | 'not_found'
  /** The server answered with any other status. */
  | 'http_error'
  /** The request was sent and got no response: offline, DNS, refused connection, or timeout. */
  | 'network'
  /** No status to read: not an axios failure, or one that never reached the network (cancel, bad config). */
  | 'unknown';

/** Wrapped errors are followed through `cause` this many levels deep, which also stops a cyclic chain. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Every client uses the fetch adapter, which reports a failed connection (refused, reset, DNS) as
 * ERR_NETWORK and a timeout as ETIMEDOUT; ECONNABORTED is the xhr adapter's abort and timeout.
 */
const NETWORK_ERROR_CODES: ReadonlySet<string> = new Set([
  AxiosError.ERR_NETWORK,
  AxiosError.ECONNABORTED,
  AxiosError.ETIMEDOUT,
]);

/** Facade answers every request with this header, and its own log lines carry the same id. */
const RESPONSE_REQUEST_ID_HEADER = 'x-request-id';
/** A uuid-like id. Anything else — a value a proxy rewrote, free text — is not reported. */
const RESPONSE_REQUEST_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
/** The only shape a backend reason code may take to be reported, e.g. `PRODUCT_NOT_FOUND_FOR_PAGE`. */
const REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]{2,63}$/;

const findAxiosError = (error: unknown, causeDepth: number): AxiosError | undefined => {
  if (isAxiosError(error)) {
    return error;
  }

  if (error instanceof Error && causeDepth < MAX_CAUSE_DEPTH) {
    return findAxiosError(error.cause, causeDepth + 1);
  }

  return undefined;
};

/** Classifies a thrown value by the HTTP response behind it — never by its message. */
export const getHttpFailureKind = (error: unknown): HttpFailureKind => {
  const axiosError = findAxiosError(error, 0);

  if (!axiosError) {
    return 'unknown';
  }

  const status = axiosError.response?.status;

  if (status !== undefined) {
    return status === 404 ? 'not_found' : 'http_error';
  }

  return axiosError.code !== undefined && NETWORK_ERROR_CODES.has(axiosError.code) ? 'network' : 'unknown';
};

/** HTTP status the server answered with, when the failure came from a response at all. */
export const getHttpFailureStatus = (error: unknown): number | undefined => findAxiosError(error, 0)?.response?.status;

const toRequestPath = (url: string | undefined): string | undefined => url?.split(/[?#]/, 1)[0] || undefined;

const toRequestHostname = (url: string | undefined, baseURL: string | undefined): string | undefined => {
  if (!url && !baseURL) {
    return undefined;
  }

  try {
    return new URL(url ?? '', baseURL).hostname || undefined;
  } catch {
    return undefined;
  }
};

/** Path of the request behind a failure, without its query string or hash. */
export const getHttpFailureRequestPath = (error: unknown): string | undefined =>
  toRequestPath(findAxiosError(error, 0)?.config?.url);

export interface HttpFailureRequest {
  /** Upper-case HTTP method. */
  method?: string;
  /** Hostname only: no scheme, port or path. */
  host?: string;
  /** Request path without its query string or hash. */
  path?: string;
}

/** The request behind a failure, with only the fields it carries; undefined when there was no request. */
export const getHttpFailureRequest = (error: unknown): HttpFailureRequest | undefined => {
  const config = findAxiosError(error, 0)?.config;

  if (!config) {
    return undefined;
  }

  const method = config.method?.toUpperCase();
  const host = toRequestHostname(config.url, config.baseURL);
  const path = toRequestPath(config.url);

  return {
    ...(method !== undefined && { method }),
    ...(host !== undefined && { host }),
    ...(path !== undefined && { path }),
  };
};

/** HTTP method of the request behind a failure, upper-cased. */
export const getHttpFailureMethod = (error: unknown): string | undefined =>
  findAxiosError(error, 0)?.config?.method?.toUpperCase() || undefined;

/**
 * Request id the backend answered with, the key to its own log lines for this request. Undefined
 * without a response, or when the header is missing or not id-shaped.
 */
export const getHttpFailureResponseRequestId = (error: unknown): string | undefined => {
  const headers = findAxiosError(error, 0)?.response?.headers;
  const requestId =
    headers instanceof AxiosHeaders ? headers.get(RESPONSE_REQUEST_ID_HEADER) : headers?.[RESPONSE_REQUEST_ID_HEADER];

  return typeof requestId === 'string' && RESPONSE_REQUEST_ID_PATTERN.test(requestId) ? requestId : undefined;
};

const UNRECOGNIZED_REASON_CODE = 'unrecognized';

const readReasonCode = (data: unknown): string | undefined => {
  const code = typeof data === 'object' && data !== null && 'code' in data ? data.code : undefined;

  if (code === undefined || code === null) {
    return undefined;
  }

  return typeof code === 'string' && REASON_CODE_PATTERN.test(code) ? code : UNRECOGNIZED_REASON_CODE;
};

/**
 * Machine-readable reason a failed response body gave (`{ code }`). Facade sends none yet
 * (SSD-7310), so this is undefined until it does. A code of any other shape than
 * `REASON_CODE_PATTERN` comes back as `unrecognized`, so free text never reaches telemetry.
 */
export const getHttpFailureReasonCode = (error: unknown): string | undefined =>
  readReasonCode(findAxiosError(error, 0)?.response?.data);

export type HttpFailureRejectionKind =
  /** Request validation turned the body down; Facade's ValidationPipe answers with one message per failed field. */
  | 'validation'
  /** A business rule turned the request down and named itself with a reason code. */
  | 'business_rule'
  /** A 4xx that says neither, such as the fixed message every cart failure carries. */
  | 'unspecified';

export interface HttpFailureRejection {
  kind: HttpFailureRejectionKind;
  /** Reason code, only when it has the shape of `REASON_CODE_PATTERN`. */
  code?: string;
  /** Names of the fields validation rejected, never their values. */
  fields?: string[];
}

/** Only a field-shaped name is reported, e.g. `lineItems[0].quantity`. */
const FIELD_NAME_PATTERN = /^[A-Za-z_][\w.[\]]{0,63}$/;
const MAX_REJECTED_FIELDS = 5;

/**
 * class-validator starts a message with its field and goes on with `must`/`should`, e.g.
 * `quantity must not be less than 1`; NestJS prefixes a nested field with its parent path.
 */
const FIELD_FIRST_MESSAGE = /^(\S+) (?:must|should)\b/;
/** Unknown properties, `each` rules and nested objects put a phrase in front of the field instead. */
const PREFIXED_FIELD_MESSAGE = /^(?:(\S+)\.)?(?:property|each value in|nested property) (\S+) (?:must|should)\b/;

const readValidationMessages = (data: unknown): unknown[] | undefined =>
  typeof data === 'object' && data !== null && 'message' in data && Array.isArray(data.message)
    ? data.message
    : undefined;

/** The field a message is about. A sentence in any other form, such as custom text, names none. */
const toRejectedField = (message: unknown): string | undefined => {
  if (typeof message !== 'string') {
    return undefined;
  }

  const sentence = message.trim();
  const prefixed = PREFIXED_FIELD_MESSAGE.exec(sentence);

  if (prefixed) {
    const [, parentPath, field] = prefixed;
    return parentPath ? `${parentPath}.${field}` : field;
  }

  return FIELD_FIRST_MESSAGE.exec(sentence)?.[1];
};

const toRejectedFields = (messages: unknown[]): string[] => {
  const fields = messages
    .map(toRejectedField)
    .filter((field): field is string => field !== undefined && FIELD_NAME_PATTERN.test(field));

  return [...new Set(fields)].slice(0, MAX_REJECTED_FIELDS);
};

const isClientErrorStatus = (status: number): boolean => 400 <= status && status < 500;

const toRejectionKind = (hasValidationMessages: boolean, hasReasonCode: boolean): HttpFailureRejectionKind => {
  if (hasValidationMessages) {
    return 'validation';
  }
  return hasReasonCode ? 'business_rule' : 'unspecified';
};

/**
 * How the server turned down a request it answered with a 4xx: the kind of rejection, its reason
 * code and the fields validation named. Only those — never a field value or the response message.
 * Undefined for any other failure: no response, or a status outside 4xx.
 */
export const getHttpFailureRejection = (error: unknown): HttpFailureRejection | undefined => {
  const response = findAxiosError(error, 0)?.response;

  if (!response || !isClientErrorStatus(response.status)) {
    return undefined;
  }

  const validationMessages = readValidationMessages(response.data);
  const reasonCode = readReasonCode(response.data);
  const code = reasonCode === UNRECOGNIZED_REASON_CODE ? undefined : reasonCode;
  const fields = validationMessages ? toRejectedFields(validationMessages) : [];

  return {
    kind: toRejectionKind(validationMessages !== undefined, reasonCode !== undefined),
    ...(code !== undefined && { code }),
    ...(fields.length > 0 && { fields }),
  };
};
